import { StyleSheet, Text, View } from '@react-pdf/renderer';
import type { LightInvoiceUsageLineItem } from '@weft/api-definition';
import { formatPrice, getTierUsagePrice } from '@weft/services';
import type { Currency } from '@weft/types';
import { isNotEmptyString } from '@weft/utils';
import { formatNumber } from '@weft/utils';
import React from 'react';
import { getPerUnitProductVariantPrice } from '../../light-invoice-utils/get-per-unit-product-variant-price';

const styles = StyleSheet.create({
  container: {
    borderBottomWidth: '1px',
    borderBottomColor: '#E8F2F8',
    paddingHorizontal: '17px',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
    gap: '3px',
    paddingVertical: '9px',
  },
  namePriceContainer: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  name: {
    fontSize: '14px',
    color: '#292D32',
    fontWeight: 500,
  },
  description: {
    fontSize: '12px',
    fontWeight: 400,
    color: '#A3ACBD',
  },
  price: {
    fontSize: '14px',
    color: '#292D32',
    fontWeight: 500,
  },
});

type UsageProductLineItemProps = {
  usageLineItem: LightInvoiceUsageLineItem;
  currency: Currency;
};

export const UsageProductLineItem: React.FC<UsageProductLineItemProps> = ({ usageLineItem, currency }) => {
  const productVariantsCount = usageLineItem.perUnitProductVariants.length + usageLineItem.tiersProductVariants.length;

  return (
    <View
      style={styles.container}
      wrap={false}
    >
      <View style={styles.namePriceContainer}>
        <Text style={styles.name}>{usageLineItem.productTitle}</Text>
        <Text style={styles.price}>{formatPrice(usageLineItem.price, currency)}</Text>
      </View>

      <View style={{ ...styles.namePriceContainer, marginTop: '5px' }}>
        {isNotEmptyString(usageLineItem.productDescription) && (
          <Text style={styles.description}>
            {usageLineItem.productDescription}
          </Text>
        )}

        <View style={{
          rowGap: '9px',
          justifyContent: 'space-between',
          width: '100%',
        }}
        >
          {usageLineItem.perUnitProductVariants.map((productVariant) => {
            const price = getPerUnitProductVariantPrice(productVariant, usageLineItem.tiersUsageMap);
            const units = usageLineItem.tiersUsageMap[productVariant.id][0].periodUnits;

            return (
              <View
                key={productVariant.id}
                style={{
                  display: 'flex',
                  flexDirection: 'row',
                  justifyContent: 'space-between',
                  fontSize: '12px',
                  fontWeight: 400,
                  color: '#A3ACBD',
                  marginLeft: 'auto',
                }}
              >
                <Text>{`${formatNumber(units)} X ${formatPrice(price, currency)}`}</Text>
              </View>
            );
          })}

          {usageLineItem.tiersProductVariants.map((productVariant) => {
            return (
              <View
                key={productVariant.id}
                style={{ width: '100%' }}
              >
                {productVariantsCount > 1 && (
                  <Text
                    style={{
                      fontSize: '12px',
                      fontWeight: 500,
                      color: '#292D32',
                      paddingBottom: '6px',
                    }}
                  >
                    {productVariant.name}
                  </Text>
                )}

                {usageLineItem.tiersUsageMap[productVariant.id]?.filter(tierUsage => tierUsage.periodUnits !== 0)
                  .sort((a, b) => a.tierIndex - b.tierIndex)
                  .map((tierUsage) => {
                    const { tierIndex, periodUnits } = tierUsage;
                    const { tiers } = productVariant.pricing.priceConfiguration;
                    const tierPrice = getTierUsagePrice(tierUsage, tiers[tierIndex]?.price ?? 0);

                    return (
                      <View
                        style={{
                          display: 'flex',
                          flexDirection: 'row',
                          justifyContent: 'flex-end',
                          fontSize: '12px',
                          fontWeight: 400,
                          color: '#A3ACBD',
                          width: '100%',
                        }}
                      >
                        <Text>{`${formatNumber(periodUnits)} X ${formatPrice(tierPrice, currency)}`}</Text>
                      </View>
                    );
                  })}
              </View>
            );
          })}
        </View>
      </View>
    </View>
  );
};